import { SlideSchedule } from "@shared/types";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { cn } from "@/lib/utils";

const DAY_LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

interface ScheduleEditorProps {
  schedule?: SlideSchedule;
  onChange: (schedule: SlideSchedule | undefined) => void;
}

/** Compact editor for a slide's optional play schedule. */
export function ScheduleEditor({ schedule, onChange }: ScheduleEditorProps) {
  const enabled = !!schedule;
  const s = schedule ?? {};

  const patch = (changes: Partial<SlideSchedule>) => onChange({ ...s, ...changes });

  function toggleDay(day: number) {
    const days = new Set(s.days ?? []);
    if (days.has(day)) days.delete(day);
    else days.add(day);
    const arr = [...days].sort((a, b) => a - b);
    patch({ days: arr.length ? arr : undefined });
  }

  return (
    <div className="rounded-lg border p-3 sm:col-span-2">
      <div className="flex items-center justify-between">
        <div>
          <Label className="text-sm">Schedule</Label>
          <p className="text-xs text-muted-foreground">
            Only show on certain dates, days, or times. Off = always.
          </p>
        </div>
        <Switch
          checked={enabled}
          onCheckedChange={(v) => onChange(v ? { ...s } : undefined)}
        />
      </div>

      {enabled && (
        <div className="mt-3 space-y-3">
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Start date</Label>
              <Input
                type="date"
                value={s.startDate ?? ""}
                onChange={(e) => patch({ startDate: e.target.value || undefined })}
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">End date</Label>
              <Input
                type="date"
                value={s.endDate ?? ""}
                onChange={(e) => patch({ endDate: e.target.value || undefined })}
              />
            </div>
          </div>

          <div className="space-y-1">
            <Label className="text-xs text-muted-foreground">Days of week (none = every day)</Label>
            <div className="flex flex-wrap gap-1">
              {DAY_LABELS.map((label, day) => {
                const active = s.days?.includes(day) ?? false;
                return (
                  <button
                    key={day}
                    type="button"
                    onClick={() => toggleDay(day)}
                    className={cn(
                      "h-8 w-10 rounded-md border text-xs",
                      active ? "bg-primary text-primary-foreground" : "bg-background hover:bg-accent",
                    )}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Start time</Label>
              <Input
                type="time"
                value={s.startTime ?? ""}
                onChange={(e) => patch({ startTime: e.target.value || undefined })}
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">End time</Label>
              <Input
                type="time"
                value={s.endTime ?? ""}
                onChange={(e) => patch({ endTime: e.target.value || undefined })}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
